"use client";

import { useState } from "react";

interface OrgAvatarProps {
  /** Org name — drives the monogram fallback. */
  name: string;
  /** Org logo URL; falls back to a monogram chip when missing or it fails to load. */
  logoUrl?: string | null;
  /** Square org icon URL; shown ahead of `logoUrl` when present, falling back to it on load error. */
  iconUrl?: string | null;
  /** Visual size preset. Defaults to "md". */
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<OrgAvatarProps["size"]>, { box: string; pad: string }> = {
  xs: { box: "size-5 text-[10px] rounded-md", pad: "p-0.5" },
  sm: { box: "size-7 text-xs rounded-lg", pad: "p-1" },
  md: { box: "size-9 text-sm rounded-lg", pad: "p-1.5" },
  lg: { box: "size-11 text-base rounded-xl", pad: "p-2" },
};

/**
 * Square organization avatar. Renders the org's icon, else its logo, *contained* on a
 * clean tile (logos are wordmarks, so they're fit-to-box, never cropped), or a neutral
 * monogram chip (first letter) when neither is available or both fail to load. Decorative by default
 * (`aria-hidden`) — pair it with adjacent text or an `aria-label` on an
 * interactive wrapper for the accessible name.
 *
 * @param props.name - Org name for the monogram fallback.
 * @param props.logoUrl - Optional logo URL; falls back to the monogram on missing/error.
 * @param props.iconUrl - Optional square icon URL; preferred over the logo, falls back to it on missing/error.
 * @param props.size - Size preset ("xs" | "sm" | "md" | "lg"); defaults to "md".
 * @param props.className - Extra classes merged onto the element.
 * @returns The contained logo tile, or a monogram fallback chip.
 */
export const OrgAvatar = ({ name, logoUrl, iconUrl, size = "md", className = "" }: OrgAvatarProps) => {
  // States
  // Keyed by URL, so a new icon/logo URL gets a fresh attempt instead of inheriting an old failure.
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(() => new Set());

  // Derived values
  const { box, pad } = SIZE_CLASSES[size];
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  const imageUrl = [iconUrl, logoUrl].find((url) => url && !failedUrls.has(url));

  // Handlers
  const handleImageError = (url: string) => setFailedUrls((previous) => new Set(previous).add(url));

  // Render
  if (imageUrl) {
    return (
      <span
        aria-hidden
        className={`${box} ${pad} flex flex-shrink-0 items-center justify-center overflow-hidden bg-white ring-1 ring-inset ring-gray-200 ${className}`}
      >
        <img
          // Keyed so falling back from icon to logo remounts the element rather than swapping `src`.
          key={imageUrl}
          src={imageUrl}
          alt=""
          // Not `lazy`: this is a small always-visible nav chip, never off-screen, so lazy-loading
          // only delays it — most visibly right after a pane swap, when it's exactly the element a
          // user is looking at.
          loading="eager"
          decoding="async"
          onError={() => handleImageError(imageUrl)}
          className="max-h-full max-w-full object-contain"
        />
      </span>
    );
  }

  return (
    <span
      aria-hidden
      className={`${box} flex flex-shrink-0 items-center justify-center bg-gray-100 font-medium text-gray-600 ${className}`}
    >
      {initial}
    </span>
  );
};
